var _jsxFileName = "/opt/atlassian/pipelines/agent/build/src/components/register/CreateNewUser.tsx";
import React, { Suspense, useCallback, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { ApiStatus } from 'Enums';
import { useAccount, useApiError, usePartner } from 'Hooks';
import { Spinner } from 'Components/utility';
import Wizard from 'Components/wizard';
import WizardMainContainer from 'Components/wizard/WizardMainContainer';
import { REGISTER, REGISTER_CLEAR } from 'Reducers/register/types';
import { logInfo } from 'Utilities/appCommunicator';
import { isBypassPhoneNumberEnabled } from 'Utilities/dataHelpers';
import i18n from 'Utilities/i18n';
import mediator from 'Utilities/mediator';
import { persistAuthSession } from 'Utilities/session';
import { store } from 'Utilities/store';
import { signIn } from 'Services/auth';
import { registerPatient } from 'Services/patient';
import RegisterAccountInfo from './steps/RegisterAccountInfo';
import RegisterConfirmCountry from './steps/RegisterConfirmCountry';
import RegisterLegalNotices from './steps/RegisterLegalNotices';
import RegisterMinorAccountInfo from './steps/RegisterMinorAccountInfo';
import RegisterMinorIntro from './steps/RegisterMinorIntro';
import RegisterMinorPersonalInfo from './steps/RegisterMinorPersonalInfo';
import RegisterPersonalInfo from './steps/RegisterPersonalInfo';
const mapStateToProps = ({
  nav: {
    query
  },
  register,
  env: {
    app,
    cid
  },
  config: {
    validation,
    adultAges
  }
}) => {
  return {
    query,
    app,
    cid,
    validation,
    adultAges,
    register,
    firstNameLimit: validation?.libreSharingApi.patient.firstName.stringMaxLength,
    lastNameLimit: validation?.libreSharingApi.patient.lastName.stringMaxLength
  };
};
const connector = connect(mapStateToProps);
const CreateNewUser = ({
  app,
  validation,
  register,
  firstNameLimit,
  lastNameLimit
}) => {
  const [loading, setLoading] = useState(false);
  const [phoneErrorTimestamp, setPhoneErrorTimestamp] = useState(0);
  const {
    showApiErrorModal
  } = useApiError();
  const {
    showEmailExistsMessage,
    showExistingProUserError,
    showPasswordValidationError,
    validateEntriesLengthAndDisplayError,
    validateDOBAndDisplayError,
    validateIsMinor,
    showFrenchZipCodeError
  } = useAccount();
  const {
    authenticateUser
  } = usePartner();
  const makeSteps = useMemo(() => {
    if (register.isMinor) {
      return [{
        component: RegisterConfirmCountry,
        componentName: 'RegisterConfirmCountry'
      }, {
        component: RegisterLegalNotices,
        componentName: 'RegisterLegalNotices'
      }, {
        component: RegisterPersonalInfo,
        componentName: 'RegisterPersonalInfo'
      }, {
        component: RegisterMinorIntro,
        componentName: 'RegisterMinorIntro'
      }, {
        component: RegisterMinorPersonalInfo,
        componentName: 'RegisterMinorPersonalInfo'
      }, {
        component: RegisterMinorAccountInfo,
        componentName: 'RegisterMinorAccountInfo'
      }];
    }
    return [{
      component: RegisterConfirmCountry,
      componentName: 'RegisterConfirmCountry'
    }, {
      component: RegisterLegalNotices,
      componentName: 'RegisterLegalNotices'
    }, {
      component: RegisterPersonalInfo,
      componentName: 'RegisterPersonalInfo'
    }, {
      component: RegisterAccountInfo,
      componentName: 'RegisterAccountInfo'
    }];
  }, [register.isMinor]);
  const handleClickSignIn = useCallback(() => {
    mediator.publish('router:navigate', '/login');
    store.dispatch({
      type: REGISTER_CLEAR
    });
  }, []);
  const validateNameLength = useCallback((firstName, lastName) => {
    const isValid = validateEntriesLengthAndDisplayError([{
      value: firstName,
      msgKey: 'CreateAccountAccountInfo.modals.accountInfoErrors.validationRules.firstNameMaxLength',
      count: firstNameLimit
    }, {
      value: lastName,
      msgKey: 'CreateAccountAccountInfo.modals.accountInfoErrors.validationRules.lastNameMaxLength',
      count: lastNameLimit
    }]);
    return isValid;
  }, [firstNameLimit, lastNameLimit, validateEntriesLengthAndDisplayError]);
  const handleApiRequestError = useCallback(error => {
    const data = error.response?.data ? error.response.data : {
      details: i18n.t('Global.modals.errorCommunicatingServer.body')
    };
    logInfo('adc-webview:create-account', {
      status: error.status,
      data
    });
    switch (error.status) {
      case ApiStatus.CONFLICT:
        {
          const code = error.response?.data.code;
          if (code === ApiStatus.FORBIDDEN_HCP_ACCOUNT) {
            showExistingProUserError();
            return;
          }
          if (code === ApiStatus.PHONE_NUMBER_ALREADY_EXISTS_CODE) {
            setPhoneErrorTimestamp(Date.now());
            return;
          }
          showEmailExistsMessage(handleClickSignIn);
          break;
        }
      case ApiStatus.BAD_REQUEST:
        {
          const code = error.response?.data.code;
          const errors = error.response?.data.errors;
          if (code === ApiStatus.INVALID_DATA_CODE) {
            showPasswordValidationError({
              validation
            }, errors, register.country);
            return;
          }
          showApiErrorModal();
          break;
        }
      default:
        showApiErrorModal();
    }
  }, [handleClickSignIn, register.country, showApiErrorModal, showEmailExistsMessage, showExistingProUserError, showPasswordValidationError, validation]);
  const handleSubmitRegister = useCallback(register => {
    return new Promise((resolve, reject) => {
      setLoading(true);
      registerPatient(register, app).then(async () => {
        const {
          accessToken,
          accountId,
          expires,
          verificationTasks
        } = await signIn(register.email, register.password, app);
        persistAuthSession({
          accessToken,
          accountId,
          expires,
          app
        });
        store.dispatch({
          type: REGISTER,
          register: {
            ...register,
            patientId: accountId
          }
        });
        if (register.country === 'US') {
          if (!isBypassPhoneNumberEnabled()) {
            const authUnverifiedTasks = verificationTasks?.filter(task => task.verified === false);
            if (authUnverifiedTasks?.length) {
              const verificationTask = authUnverifiedTasks[0];
              mediator.publish('router:navigate', {
                url: '/verify-number',
                data: {
                  taskID: verificationTask.id,
                  mobileNumber: verificationTask.verificationTarget,
                  onSuccessfulVerify: async () => {
                    await authenticateUser(register.email, register.password, false);
                    mediator.publish('router:navigate', '/hipaa');
                  }
                }
              });
              return;
            }
          }
          mediator.publish('router:navigate', '/hipaa');
          resolve();
          return;
        }
        mediator.publish('router:navigate', '/rwe');
        resolve();
      }).catch(error => {
        handleApiRequestError(error);
        reject(error);
      }).finally(() => {
        setLoading(false);
      });
    });
  }, [app, authenticateUser, handleApiRequestError]);
  return /*#__PURE__*/React.createElement(React.Fragment, {
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 262,
      columnNumber: 5
    }
  }, /*#__PURE__*/React.createElement(Suspense, {
    fallback: /*#__PURE__*/React.createElement(Spinner, {
      __self: this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 263,
        columnNumber: 27
      }
    }),
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 263,
      columnNumber: 7
    }
  }, /*#__PURE__*/React.createElement(WizardMainContainer, {
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 264,
      columnNumber: 9
    }
  }, /*#__PURE__*/React.createElement(Wizard, {
    steps: makeSteps,
    handleSubmitRegister: handleSubmitRegister,
    handleClickSignIn: handleClickSignIn,
    validateNameLength: validateNameLength,
    validateIsMinor: validateIsMinor,
    validateDOBAndDisplayError: validateDOBAndDisplayError,
    showFrenchZipCodeError: showFrenchZipCodeError,
    initialValues: register,
    currentStep: register.currentStep,
    loading: loading,
    phoneErrorTimestamp: phoneErrorTimestamp,
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 265,
      columnNumber: 11
    }
  }))));
};
export default connector(CreateNewUser);